import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircle } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  useCreateTemplate,
  useTemplateQuery,
  useUpdateTemplateFull,
} from "../api/queries";
import { TemplateFormTour, useTemplateFormTour } from "../tour/TemplateFormTour";
import { TemplateForm, type TemplateFormValues } from "../ui/TemplateForm";

export function TemplateCreatePage() {
  const params = useParams({ strict: false });
  const idParam = params.id as string | undefined;
  const isEdit = !!idParam;
  const nav = useNavigate();
  const tour = useTemplateFormTour();

  const canCreate = useActionPermission(["template:create"]);
  const canUpdate = useActionPermission(["template:update"]);
  const hasPermission = isEdit ? canUpdate : canCreate;

  const template = useTemplateQuery(idParam ?? "");
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplateFull(idParam ?? "");

  if (!hasPermission) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">
                {isEdit
                  ? "ທ່ານບໍ່ມີສິດແກ້ໄຂຮ່າງເອກະສານ"
                  : "ທ່ານບໍ່ມີສິດສ້າງຮ່າງເອກະສານ"}
              </p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (isEdit && template.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (isEdit && !template.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">ບໍ່ພົບຮ່າງເອກະສານ</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  const templateData = template.data;

  // Convert template data to form values
  const initialValues: Partial<TemplateFormValues> | undefined = isEdit
    ? {
        name: templateData?.name,
        categoryId: templateData?.categoryId ?? undefined,
        description: templateData?.description ?? undefined,
        usageNotes: templateData?.usageNotes ?? undefined,
        departmentIds: templateData?.departments?.map((d) => d.departmentId) ?? [],
        fileKey: null, // Don't pre-populate file - upload first
        version: undefined, // New version will be generated if file is uploaded
        changelog: undefined,
        keepOldVersionsActive: true,
      }
    : undefined;

  const handleSubmit = async (values: TemplateFormValues) => {
    // Convert form values to API format (null -> undefined for file)
    const apiValues = {
      ...values,
      fileKey: values.fileKey ?? undefined,
    };

    try {
      if (isEdit && idParam) {
        await updateMutation.mutateAsync(apiValues as never);
        // Redirect to detail page after successful update
        nav({ to: `/app/template/${idParam}` });
      } else {
        const result = await createMutation.mutateAsync(apiValues as never);
        nav({ to: `/app/template/${result.id}` });
      }
    } catch (error) {
      // Error is already handled by the mutation and shown via toast
      // Don't redirect on error - stay on the form page
      console.error(`Failed to ${isEdit ? "update" : "create"} template:`, error);
    }
  };

  return (
    <>
      <Header />
      <Main>
        <TemplateFormTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-bold text-3xl">
                    {isEdit ? "ແກ້ໄຂຮ່າງເອກະສານ" : "ເພີ່ມຮ່າງເອກະສານໃໝ່"}
                  </h1>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 rounded-full"
                    onClick={tour.startTour}
                  >
                  <HelpCircle className="size-5" />
                  </Button>
                </div>
                <p className="mt-1 text-muted-foreground">
                  {isEdit
                    ? "ແກ້ໄຂຂໍ້ມູນຮ່າງເອກະສານ ຫຼື ອັບເດດເວີຊັນ"
                    : "ສ້າງຮ່າງເອກະສານໃໝ່"}
                </p>
              </div>

            </div>
            <Button
              variant="outline"
              onClick={() => {
                if (isEdit && idParam) {
                  nav({ to: `/app/template/${idParam}` });
                } else {
                  nav({ to: "/app/template" });
                }
              }}
            >
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບ
            </Button>
          </div>

          {/* Form */}
          <TemplateForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            submitting={createMutation.isPending || updateMutation.isPending}
            isEdit={isEdit}
          />
        </div>
      </Main>
    </>
  );
}
